import {
  Component,
  createSignal,
  createMemo,
  createEffect,
  onCleanup,
  Show,
} from "solid-js";
import { AiErrorBoundary, Button } from "../ui";
import { EnrichmentPanel } from "../enrichment";
import { TypeSelector, TypeSpecificFields } from "./quick-create";
import type {
  WorkItemType,
  WorkItemTypeId,
  WorkItemStatusId,
  CreateWorkItemRequest,
  ProjectId,
  LabelSuggestion,
  Priority,
  SubtaskProposal,
} from "../../api";

export interface QuickCreateCardProps {
  /** Project ID for AI enrichment context */
  projectId: ProjectId;
  /** Status ID to assign to the new item */
  statusId: WorkItemStatusId;
  /** Available work item types to choose from */
  itemTypes: WorkItemType[];
  /** Loading state while creating */
  loading?: boolean;
  /** Callback when create is submitted */
  onCreate: (data: CreateWorkItemRequest) => void;
  /** Callback when cancelled */
  onCancel: () => void;
}

/**
 * QuickCreateCard - Inline card creation form in a column
 *
 * Features:
 * - Title input field (auto-focused)
 * - Type selector dropdown
 * - Enter to create, Escape to cancel
 * - Auto-assigns status based on column
 * - Loading state during creation
 */
export const QuickCreateCard: Component<QuickCreateCardProps> = (props) => {
  const [title, setTitle] = createSignal("");
  const [selectedLabels, setSelectedLabels] = createSignal<LabelSuggestion[]>(
    [],
  );
  const [selectedPriority, setSelectedPriority] =
    createSignal<Priority>("none");
  const [description, setDescription] = createSignal("");

  const [aiClassifiedType, setAiClassifiedType] =
    createSignal<WorkItemTypeId | null>(null);
  const [userOverrideType, setUserOverrideType] =
    createSignal<WorkItemTypeId | null>(null);
  const [aiClassificationLoading, setAiClassificationLoading] =
    createSignal(false);

  const [typeContextFields, setTypeContextFields] = createSignal<
    Record<string, string>
  >({});

  const [selectedSubtasks, setSelectedSubtasks] = createSignal<
    SubtaskProposal[]
  >([]);

  // Computed: user selection takes precedence over AI classification
  const effectiveType = createMemo(() => {
    const userType = userOverrideType();
    if (userType) return userType;

    const aiType = aiClassifiedType();
    if (aiType) return aiType;

    const defaultType =
      props.itemTypes.find((t) => t.name === "Task") ?? props.itemTypes[0];
    return defaultType?.id ?? "";
  });

  // Clear type-specific fields when the effective type changes
  // This prevents stale data from a previous type being submitted
  createEffect((prevType: WorkItemTypeId | undefined) => {
    const currentType = effectiveType();
    if (prevType !== undefined && prevType !== currentType) {
      setTypeContextFields({});
    }
    return currentType;
  });

  // Auto-focus input when element is assigned
  // Uses double requestAnimationFrame to ensure focus happens AFTER Modal's own focus management
  // Modal uses single RAF to focus first focusable (close button), so we need to run after that
  const setInputRef = (el: HTMLInputElement | undefined) => {
    if (el) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => el.focus());
      });
    }
  };

  // 5-second timeout: default to Task if AI classification is slow
  let classificationTimeoutTimer: ReturnType<typeof setTimeout> | undefined;

  createEffect(() => {
    // Clear existing timer on any relevant state change
    if (classificationTimeoutTimer) {
      clearTimeout(classificationTimeoutTimer);
      classificationTimeoutTimer = undefined;
    }

    const titleLen = title().length;
    const hasUserOverride = userOverrideType();
    const hasAiType = aiClassifiedType();

    // Only set timeout if:
    // 1. Title is long enough to trigger enrichment (>= 3 chars)
    // 2. No user override yet
    // 3. No AI classification yet
    if (titleLen >= 3 && !hasUserOverride && !hasAiType) {
      classificationTimeoutTimer = setTimeout(() => {
        // Only default if still no type selected
        if (!userOverrideType() && !aiClassifiedType()) {
          const taskType = props.itemTypes.find((t) => t.name === "Task");
          if (taskType) {
            setAiClassifiedType(taskType.id);
          }
        }
      }, 5000);
    }
  });

  onCleanup(() => {
    if (classificationTimeoutTimer) {
      clearTimeout(classificationTimeoutTimer);
    }
  });

  const isValid = () => title().trim().length > 0;

  const handleSubmit = () => {
    if (!isValid() || props.loading) return;

    // Get label IDs from selected suggestions (only existing labels)
    const labelIds = selectedLabels()
      .filter((l): l is LabelSuggestion & { existing_id: string } =>
        Boolean(l.existing_id),
      )
      .map((l) => l.existing_id);

    const typeContext = typeContextFields();
    const hasTypeContext = Object.values(typeContext).some(
      (v) => v.trim().length > 0,
    );

    const subtasks = selectedSubtasks().map((s) => ({
      title: s.title,
      description: s.description,
      priority: s.priority,
    }));

    const data: CreateWorkItemRequest = {
      title: title().trim(),
      description: description().trim() || undefined,
      item_type_id: effectiveType() || undefined,
      status_id: props.statusId,
      priority: selectedPriority(),
      labels: labelIds.length > 0 ? labelIds : undefined,
      type_context: hasTypeContext ? typeContext : undefined,
      subtasks: subtasks.length > 0 ? subtasks : undefined,
    };

    props.onCreate(data);
  };

  const handleTypeClassified = (
    suggestedType: string | null,
    isLoading: boolean,
  ) => {
    setAiClassificationLoading(isLoading);

    // Don't update AI classification if user has already made a selection
    if (userOverrideType()) return;

    if (suggestedType) {
      // Find the type ID that matches the AI suggestion (e.g., "bug" -> Bug type)
      const matchedType = props.itemTypes.find(
        (t) => t.name.toLowerCase() === suggestedType.toLowerCase(),
      );
      if (matchedType) {
        setAiClassifiedType(matchedType.id);
      }
    } else if (!isLoading) {
      // Clear AI classification if no type suggested and not loading
      setAiClassifiedType(null);
    }
  };

  const handleTypeSelect = (typeId: WorkItemTypeId) => {
    setUserOverrideType(typeId);
    setAiClassifiedType(null); // Clear AI classification to remove the ai tag
  };

  const handleTypeContextChange = (fieldName: string, value: string) => {
    setTypeContextFields((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Enter is also how a keyboard user activates whichever button they have
    // focused — applying a suggestion, undoing one, Cancel. Submitting the
    // form instead would swallow that activation entirely.
    const onButton = (e.target as HTMLElement | null)?.closest("button");

    if (e.key === "Enter" && !e.shiftKey && !onButton) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      props.onCancel();
    }
  };

  const selectedType = () =>
    props.itemTypes.find((t) => t.id === effectiveType());

  return (
    <div
      class="bg-surface rounded border border-line p-3 animate-in fade-in-0 slide-in-from-top-1 duration-150"
      onKeyDown={handleKeyDown}
    >
      {/* Title Input */}
      <div class="mb-3">
        <input
          ref={setInputRef}
          type="text"
          value={title()}
          onInput={(e) => setTitle(e.currentTarget.value)}
          placeholder="Enter a title..."
          disabled={props.loading}
          class="w-full px-0 py-1 text-sm font-medium text-ink border-0 border-b border-line bg-transparent rounded-none focus:border-ink placeholder:text-ink-3 disabled:text-ink-3 disabled:cursor-not-allowed"
        />
      </div>

      {/* Type Selector */}
      <div class="mb-3">
        <label class="block text-xs font-medium text-ink-2 mb-1">Type</label>
        <TypeSelector
          types={props.itemTypes}
          selectedType={effectiveType()}
          aiSuggestedType={aiClassifiedType() ?? undefined}
          isAnalyzing={aiClassificationLoading()}
          disabled={props.loading}
          onSelect={handleTypeSelect}
        />
      </div>

      {/* Type-Specific Fields */}
      <Show when={selectedType()} keyed>
        {(type) => (
          <div class="mb-3">
            <TypeSpecificFields
              typeName={type.name}
              values={typeContextFields()}
              onChange={handleTypeContextChange}
              disabled={props.loading}
            />
          </div>
        )}
      </Show>

      {/* AI Enrichment Panel - shows after 3+ characters */}
      {/* Modal approach eliminates layout shift - panel renders naturally */}
      <Show when={title().length >= 3}>
        <div class="mb-3">
          <AiErrorBoundary
            surface="AI suggestions"
            manualPath="You can still fill in the fields yourself and create the item."
          >
            <EnrichmentPanel
              projectId={props.projectId}
              title={title()}
              description={description()}
              debounceMs={300}
              onLabelsSelected={setSelectedLabels}
              onPrioritySelected={setSelectedPriority}
              onEnhancedTitle={setTitle}
              onSuggestedDescription={setDescription}
              onSubtasksSelectionChange={setSelectedSubtasks}
              onTypeClassified={handleTypeClassified}
            />
          </AiErrorBoundary>
        </div>
      </Show>

      {/* Action Buttons */}
      <div class="flex items-center justify-between">
        <p class="text-xs text-ink-3">
          <kbd class="px-1 py-0.5 font-mono text-[10px] bg-sunken border border-line rounded-sm text-ink-2">
            Enter
          </kbd>{" "}
          to create,{" "}
          <kbd class="px-1 py-0.5 font-mono text-[10px] bg-sunken border border-line rounded-sm text-ink-2">
            Esc
          </kbd>{" "}
          to cancel
        </p>
        <div class="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={props.onCancel}
            disabled={props.loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            loading={props.loading}
            disabled={!isValid()}
          >
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickCreateCard;
